import { useRef, useCallback, useEffect } from "react";
import { stripMathDelimiters, tryRenderMathToString } from "@ever-tutor/et-ui";
import { useRuntimeContext } from "@/features/tutor/context/RuntimeContext";
import { useUIStore } from "@/features/tutor/store/useUIStore";
import {
  selectIsAssessmentActive,
  useAssessmentStore,
} from "@/features/tutor/store/useAssessmentStore";
import { debugLog } from "@/utils/debugLog";
import { usePlayerTick } from "@/features/tutor/hooks/usePlayerTick";
import {
  noteSpeechEnd,
  pauseSilentTurn,
  resumeSilentTurn,
} from "@/features/tutor/utils/silentTurnClock";

/**
 * One spoken word in a CAPTIONS_UPDATE payload. Mirrors backend
 * `WordAlignment` after the reconciler has merged it onto a Transcription.
 */
type Word = {
  text: string;
  start_ms: number;
  duration_ms: number;
};

/**
 * One CAPTIONS_UPDATE chunk = one Token. The backend emits one chunk per
 * fully-timestamped Token (see backend/pipeline/segmenter/chunker.py).
 *   - `caption_text` is what the frontend displays (LaTeX for math, plain
 *     text otherwise).
 *   - `is_math` toggles KaTeX rendering.
 *   - `words` carries per-word alignment so plain prose tokens stream
 *     word-by-word and math tokens can pick a render time.
 */
export type CaptionsToken = {
  caption_text: string;
  is_math: boolean;
  flush: boolean;
  words: Word[];
};

const SENTENCE_END_PUNCT_RE = /[.!?]/;
const FREEZE_DEBOUNCE_MS = 200;

export const useCaptionsQueue = () => {
  const captionsQueueRef = useRef<string[]>([]);
  const isPausedRef = useRef(false);
  const { subscribe, pcmPlayerRef } = useRuntimeContext();
  const { setCurrentCaptions } = useUIStore();
  const setLastAgentResponse = useUIStore((s) => s.setLastAgentResponse);
  const setCaptionsFrozen = useUIStore((s) => s.setCaptionsFrozen);
  const setCaptionsExpanded = useUIStore((s) => s.setCaptionsExpanded);
  const addCaptionsSnapshot = useUIStore((s) => s.addCaptionsSnapshot);
  const clearCaptionsSnapshots = useUIStore((s) => s.clearCaptionsSnapshots);
  const responseBufferRef = useRef<string>("");
  const scheduledClearRef = useRef(false);
  const isInactivityTurnRef = useRef(false);
  const tokenStatsRef = useRef<{
    tokenCount: number;
    wordCount: number;
    minMs: number;
    maxMs: number;
  }>({ tokenCount: 0, wordCount: 0, minMs: Infinity, maxMs: 0 });

  const {
    queueRef: pendingRef,
    enqueue,
    cancelPending,
    flushAll,
    ensureTicking,
  } = usePlayerTick(pcmPlayerRef, {
    isPausedRef,
  });

  // Set when audio ends naturally — late-arriving CAPTIONS_UPDATE chunks
  // apply immediately instead of being scheduled against playedMs.
  const audioEndedRef = useRef(false);

  // Debounce timer for setCaptionsFrozen. Resets every time a word lands
  // after audio ends, so the freeze only fires once captions go quiet for
  // FREEZE_DEBOUNCE_MS — ensuring late-arriving words get a pill and the
  // snapshot includes all words.
  const freezeDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleFreeze = useCallback(() => {
    if (freezeDebounceRef.current !== null) {
      clearTimeout(freezeDebounceRef.current);
    }
    freezeDebounceRef.current = setTimeout(() => {
      freezeDebounceRef.current = null;
      if (isInactivityTurnRef.current) return;
      if (!responseBufferRef.current) return;
      const { currentCaptions: finalCaptions, captionsSnapshots } =
        useUIStore.getState();
      if (finalCaptions.length > 0) {
        const lastSnap = captionsSnapshots[captionsSnapshots.length - 1] as
          | string[]
          | undefined;
        if (lastSnap?.join(" ") !== finalCaptions.join(" ")) {
          addCaptionsSnapshot(finalCaptions);
        }
      }
      setLastAgentResponse(responseBufferRef.current);
      setCaptionsFrozen(true);
    }, FREEZE_DEBOUNCE_MS);
  }, [addCaptionsSnapshot, setLastAgentResponse, setCaptionsFrozen]);

  // Schedules a single text token to land in the captions queue at start_ms.
  // Used for both plain words (per-word) and math tokens (one render at the
  // first word's start_ms).
  const scheduleCaptionInsertion = useCallback(
    (displayText: string, startMs: number) => {
      const applyFn = () => {
        if (scheduledClearRef.current) {
          scheduledClearRef.current = false;
          setCurrentCaptions([displayText]);
          captionsQueueRef.current = [displayText];
          if (SENTENCE_END_PUNCT_RE.test(displayText)) {
            scheduledClearRef.current = true;
            addCaptionsSnapshot([displayText]);
          }
          if (audioEndedRef.current) scheduleFreeze();
          return;
        }

        if (SENTENCE_END_PUNCT_RE.test(displayText)) {
          scheduledClearRef.current = true;
        }

        const currentCaptions = useUIStore.getState().currentCaptions;
        const newCaptions = [...currentCaptions, displayText];
        setCurrentCaptions(newCaptions);
        captionsQueueRef.current = newCaptions;

        if (scheduledClearRef.current) {
          addCaptionsSnapshot(newCaptions);
        }

        if (audioEndedRef.current) scheduleFreeze();
      };

      if (audioEndedRef.current) {
        applyFn();
      } else {
        enqueue(startMs, applyFn);
      }
    },
    [enqueue, setCurrentCaptions, addCaptionsSnapshot, scheduleFreeze],
  );

  // Renders a math token: try KaTeX up front so we know whether to schedule
  // one math caption (KaTeX HTML success) or fall back to per-word scheduling.
  const scheduleMathToken = useCallback(
    (token: CaptionsToken) => {
      if (!token.words.length) return;
      const firstStart = token.words[0].start_ms;

      // Strip delimiters + trailing punctuation (e.g. "\(7x^6\).") so the
      // precheck doesn't fall back to word-streaming just for a trailing period.
      const stripped = stripMathDelimiters(token.caption_text);
      const innerLatex = stripped
        ? stripped.latex
        : token.caption_text.slice(2, -2);
      const canRenderMath = tryRenderMathToString(innerLatex).ok;

      if (canRenderMath) {
        // KaTeX rendered cleanly — schedule the caption_text as one entry.
        // Captions component decides whether to render LaTeX vs plain.
        scheduleCaptionInsertion(token.caption_text, firstStart);
        return;
      }

      // Fall back: stream the spoken words individually.
      token.words.forEach((word) => {
        scheduleCaptionInsertion(word.text.trim(), word.start_ms);
      });
    },
    [scheduleCaptionInsertion],
  );

  const pauseTutorCaptions = useCallback(() => {
    isPausedRef.current = true;
    // A muted turn has no audio graph for the modal to suspend, so its clock
    // is a second timeline nothing else here was stopping — see
    // silentTurnClock's pauseSilentTurn for what that leaves running.
    pauseSilentTurn();
  }, []);

  const resumeTutorCaptions = useCallback(() => {
    isPausedRef.current = false;
    resumeSilentTurn();
    ensureTicking();
  }, [ensureTicking]);

  const resetPlayedClock = useCallback(() => {
    const player = pcmPlayerRef?.current;
    if (player) {
      player.playedMs = 0;
      player.silentGapMs = 0;
      // Must clear the anchor too: playedMs is now derived from audioT0 on
      // every tick, so zeroing it against a stale anchor is undone by the
      // next tick. Null means "no audio yet this generation" — the clock
      // holds at 0 until the next flush() re-anchors it.
      player.audioT0 = null;
    }
  }, [pcmPlayerRef]);

  useEffect(() => {
    // rAF does not run while the tab is hidden, so the queue stops draining.
    // Recompute the clock on the way back so the first frame drains against a
    // current value rather than a stale one.
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        pcmPlayerRef?.current?.tickPlayedMs();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [pcmPlayerRef]);

  const clearQueue = useCallback(() => {
    if (freezeDebounceRef.current !== null) {
      clearTimeout(freezeDebounceRef.current);
      freezeDebounceRef.current = null;
    }
    setCurrentCaptions([]);
    setLastAgentResponse("");
    setCaptionsFrozen(false);
    setCaptionsExpanded(false);
    clearCaptionsSnapshots();
    responseBufferRef.current = "";
    captionsQueueRef.current = [];
    isInactivityTurnRef.current = false;
    audioEndedRef.current = false;
    cancelPending();
  }, [
    setCurrentCaptions,
    setLastAgentResponse,
    setCaptionsFrozen,
    setCaptionsExpanded,
    clearCaptionsSnapshots,
    cancelPending,
  ]);

  useEffect(() => {
    // Canvas turns never fire START/TURN_CHANGE, so this is their only reset point.
    const handleCanvasTurnStart = () => {
      clearQueue();
      resetPlayedClock();
      const player = pcmPlayerRef?.current;
      if (player) player.audioT0 = null;
    };
    window.addEventListener("canvas:turn-start", handleCanvasTurnStart);
    return () =>
      window.removeEventListener("canvas:turn-start", handleCanvasTurnStart);
  }, [clearQueue, resetPlayedClock, pcmPlayerRef]);

  useEffect(() => {
    const unsubCaptionsUpdate = subscribe(
      "CAPTIONS_UPDATE",
      (chunk: { chunk_data: string; turn_id: string }) => {
        let token: CaptionsToken;
        try {
          token = JSON.parse(chunk.chunk_data) as CaptionsToken;
        } catch (error) {
          console.error("Error parsing CAPTIONS_UPDATE chunk:", error);
          return;
        }
        if (!token.words || token.words.length === 0) return;

        const tokenStart = token.words[0].start_ms;
        const tokenEnd =
          token.words[token.words.length - 1].start_ms +
          token.words[token.words.length - 1].duration_ms;

        // A muted turn has no audio to run out, so this is what tells the
        // silent clock when the turn stops talking. It is read here, before
        // the assessment guard and before anything is scheduled, because the
        // timeline belongs to the turn rather than to what we choose to show
        // of it — and because what gets scheduled is often one onset for a
        // token that lasts seconds.
        noteSpeechEnd(tokenEnd);

        const { activeAssessmentModal } = useUIStore.getState();
        if (
          activeAssessmentModal ||
          selectIsAssessmentActive(useAssessmentStore.getState())
        )
          return;

        // Stats for [TURN_END] debug logging.
        const stats = tokenStatsRef.current;
        stats.tokenCount += 1;
        stats.wordCount += token.words.length;
        if (tokenStart < stats.minMs) stats.minMs = tokenStart;
        if (tokenEnd > stats.maxMs) stats.maxMs = tokenEnd;

        if (token.is_math) {
          responseBufferRef.current = responseBufferRef.current
            ? responseBufferRef.current + " " + token.caption_text
            : token.caption_text;
          scheduleMathToken(token);
        } else {
          // Plain prose: one Token = one Transcription (the encoder emits
          // per-word). Display `caption_text` rather than `word.text` so
          // language transforms (e.g. Devanagari→Roman) reach the user —
          // `word.text` is the TTS-aligned spoken form, which intentionally
          // stays in the source script.
          const trimmed = token.caption_text.trim();
          if (!trimmed) return;
          responseBufferRef.current = responseBufferRef.current
            ? responseBufferRef.current + " " + trimmed
            : trimmed;
          scheduleCaptionInsertion(trimmed, token.words[0].start_ms);
        }
      },
    );

    const unsubTurnChange = subscribe(
      "TURN_CHANGE",
      (opts?: { source?: string }) => {
        isPausedRef.current = false;
        audioEndedRef.current = false;
        cancelPending();
        resetPlayedClock();
        tokenStatsRef.current = {
          tokenCount: 0,
          wordCount: 0,
          minMs: Infinity,
          maxMs: 0,
        };
        scheduledClearRef.current = false;
        // Reset all caption display state (also resets isInactivityTurnRef → false)
        clearQueue();
        // Stamp after clearQueue so the flag survives until AUDIO_PLAYBACK_ENDED
        isInactivityTurnRef.current = opts?.source === "inactivity";
      },
    );

    const unsubStart = subscribe("START", () => {
      // Cancel any pending captions from the previous generation and reset
      // the played-time clock. Each generation restarts word timestamps near
      // zero, so playedMs must be reset per generation.
      cancelPending();
      resetPlayedClock();
      // Clear audioEndedRef so words from this generation are scheduled
      // normally — not flushed immediately. Needed for slide navigation
      // where TURN_CHANGE may not fire before the new slide's START.
      audioEndedRef.current = false;
      // audioT0 is set once on the turn's first flush and is NOT reset between
      // generations. Null it so this generation's first flush re-stamps it.
      const player = pcmPlayerRef?.current;
      if (player) player.audioT0 = null;
    });

    const unsubReset = subscribe("RESET", () => {
      // No char-offset state to clear under the new protocol.
    });

    const unsubAudioPlaybackEnded = subscribe("AUDIO_PLAYBACK_ENDED", () => {
      audioEndedRef.current = true;
      flushAll();
      const stats = tokenStatsRef.current;
      const pendingCount = pendingRef.current.length;
      const playedMs = pcmPlayerRef?.current?.playedMs ?? 0;
      debugLog(
        `[TURN_END] captions: tokens=${stats.tokenCount} words=${stats.wordCount} range=[${stats.minMs === Infinity ? 0 : stats.minMs}-${stats.maxMs} ms] | audio: playedMs=${Math.round(playedMs)} ms | pendingWords=${pendingCount}`,
      );
      tokenStatsRef.current = {
        tokenCount: 0,
        wordCount: 0,
        minMs: Infinity,
        maxMs: 0,
      };
      scheduledClearRef.current = false;
      // Inactivity/re-engagement messages: clear live captions and don't freeze —
      // they should vanish after being spoken and not overwrite the persistent bubble.
      if (isInactivityTurnRef.current) {
        setCurrentCaptions([]);
        setCaptionsFrozen(false);
        clearCaptionsSnapshots();
        return;
      }
      if (responseBufferRef.current) {
        // Debounced: waits FREEZE_DEBOUNCE_MS after the last word lands before
        // taking the snapshot and freezing. Each late-arriving word resets the
        // timer, so the snapshot always includes all words and the pill visits
        // each one before the captions switch to sticky mode.
        scheduleFreeze();
      }
    });

    // Canvas audio ending — mirrors AUDIO_PLAYBACK_ENDED for the notepad pipeline.
    const handleCanvasAudioEnded = () => {
      audioEndedRef.current = true;
      flushAll();
      scheduledClearRef.current = false;
      if (responseBufferRef.current) {
        scheduleFreeze();
      }
    };
    window.addEventListener(
      "canvas:audio-playback-ended",
      handleCanvasAudioEnded,
    );

    return () => {
      unsubCaptionsUpdate();
      unsubTurnChange();
      unsubStart();
      unsubReset();
      unsubAudioPlaybackEnded();
      cancelPending();
      window.removeEventListener(
        "canvas:audio-playback-ended",
        handleCanvasAudioEnded,
      );
    };
  }, [
    subscribe,
    scheduleCaptionInsertion,
    scheduleMathToken,
    cancelPending,
    flushAll,
    clearQueue,
    scheduleFreeze,
    setCurrentCaptions,
    setLastAgentResponse,
    setCaptionsFrozen,
    clearCaptionsSnapshots,
    resetPlayedClock,
    addCaptionsSnapshot,
    pcmPlayerRef,
    pendingRef,
  ]);

  return {
    clearQueue,
    resetAudioStartTime: resetPlayedClock,
    pauseTutorCaptions,
    resumeTutorCaptions,
  };
};
